import { useState } from 'react';
import { CheckCircle2, ExternalLink, Store } from 'lucide-react';
import type { Questionnaire, QuestionnaireQuestion } from '@peles/shared';
import { StatusBadge } from '../common';
import { formatAnswerValue, hasAnswerValue } from './answer-format';
import { useApplyQuestionnaireAnswer } from '../../hooks/useQuestionnaires';

function applyActionLabel(question: QuestionnaireQuestion): string | null {
  const link = question.dataLink;
  if (!link) return null;
  switch (link.kind) {
    case 'EVENT_FIELD':
      if (link.field === 'startAt') return 'Update event date';
      if (link.field === 'guestCount') return 'Update guest count';
      return null; // venue is prefill-only
    case 'EVENT_TAG':
      return `Update tag "${link.tagCode}"`;
    case 'TIMELINE_TIME':
      return question.timelineEntryId ? 'Update timeline time' : null;
    case 'VENDOR_LINK':
      return question.answer?.vendorId ? 'Add vendor to event' : null;
  }
}

export interface QuestionnaireReviewViewProps {
  questionnaire: Questionnaire;
}

interface ApplyFeedback {
  questionId: string;
  kind: 'success' | 'error';
  message: string;
}

/**
 * Producer review of client answers: each linked answer can be applied back
 * onto the event data (date, tags, timeline times, vendor list) in one click.
 * Apply state is tracked per question so one in-flight apply never silently
 * disables the others, and feedback is shown inline next to the question.
 */
export function QuestionnaireReviewView({ questionnaire }: QuestionnaireReviewViewProps) {
  const applyAnswer = useApplyQuestionnaireAnswer(questionnaire.id);
  const [pendingQuestionId, setPendingQuestionId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<ApplyFeedback | null>(null);

  function handleApply(questionId: string) {
    setPendingQuestionId(questionId);
    setFeedback(null);
    applyAnswer.mutate(questionId, {
      onSuccess: (result) => setFeedback({ questionId, kind: 'success', message: result.applied }),
      onError: (error) =>
        setFeedback({
          questionId,
          kind: 'error',
          message: error instanceof Error ? error.message : 'Failed to apply answer',
        }),
      onSettled: () => setPendingQuestionId(null),
    });
  }

  return (
    <div className="space-y-6">
      {questionnaire.sections.map((section) => (
        <div key={section.id} className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">{section.name}</h3>
          </div>
          <ul className="divide-y divide-gray-50">
            {section.questions.map((question) => {
              const answered = hasAnswerValue(question.answer?.value);
              const display = formatAnswerValue(
                question.questionType,
                question.answer?.value,
                question.answer?.vendorBusinessName,
              );
              const actionLabel = applyActionLabel(question);
              const isApplied = !!question.answer?.appliedAt;
              const isApplying = pendingQuestionId === question.id;
              const questionFeedback = feedback?.questionId === question.id ? feedback : null;
              return (
                <li key={question.id} className="px-4 py-3">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">{question.prompt}</p>
                      <p
                        className={`mt-0.5 text-sm ${answered ? 'text-gray-900' : 'text-gray-400 italic'}`}
                        data-testid={`answer-${question.id}`}
                      >
                        {answered ? display : 'Not answered'}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {question.answer?.vendorId && (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <Store size={12} />
                            {question.answer.vendorBusinessName ?? 'Linked vendor'}
                          </span>
                        )}
                        {isApplied && <StatusBadge label="Applied" color="green" />}
                      </div>
                      {questionFeedback && (
                        <p
                          className={`mt-1.5 text-xs ${
                            questionFeedback.kind === 'success' ? 'text-green-700' : 'text-red-600'
                          }`}
                          data-testid={`apply-feedback-${question.id}`}
                        >
                          {questionFeedback.message}
                        </p>
                      )}
                    </div>
                    {answered && actionLabel && (
                      <button
                        onClick={() => handleApply(question.id)}
                        disabled={isApplying}
                        className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                          isApplied
                            ? 'text-gray-500 border-gray-200 hover:bg-gray-50'
                            : 'text-primary-700 border-primary-200 hover:bg-primary-50'
                        } disabled:opacity-50`}
                        data-testid={`apply-${question.id}`}
                      >
                        {isApplied ? <CheckCircle2 size={14} /> : <ExternalLink size={14} />}
                        {isApplying ? 'Applying…' : isApplied ? 'Re-apply' : actionLabel}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
